import { makeStyles, Grid, Paper, MenuItem, TextField } from "@material-ui/core";
import { UserType } from "@ofqual/portal-client-shell/dist/module/redux/state";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { useAuth } from "@ofqual/portal-core/core/hooks";
import { QueryParams } from "@ofqual/portal-core/core/types";
import React, { useEffect, useMemo, useState } from "react";
import { EmbedConfig } from "../../domain/EmbedConfig";
import { ReportConfig } from "../../domain/ReportConfig";
import { authSelector } from "../../redux/selectors";
import { aoService, reportsService } from "../../services";
import { ModuleAppBar } from "../ModuleAppBar";
import { ReportViewer } from "./ReportViewer";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.contentOffsets.AFTER_SECONDARY_APPBAR,
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

const Reports: React.FC<{}> = () => {
  const classes = useStyles();
  const { userType, token } = useAuth(authSelector);
  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoService.search(params || {}, { token }),
    }),
    [token]
  );

  const [selectedAo, setSelectedAo] = useState<string | undefined>(undefined);
  const [selectedReportId, setSelectedReportId] = useState<string | undefined>(undefined);
  const [embedConfig, setEmbedConfig] = useState<EmbedConfig | null>(null);
  const [availableReports, setAvailableReports] = useState<ReportConfig[]>([]);

  useEffect(() => {
    reportsService.getReports(selectedAo || null, { token })
      .then(res => setAvailableReports(res))
      .catch(err => console.error(err));
  }, [selectedAo, token, setAvailableReports]);

  useEffect(() => {
    if (selectedReportId != null) {
      const report = availableReports.find(x => x.reportId === selectedReportId)!;

      if (!!report) {
        reportsService.getEmbedConfig(
          report.recognitionNumber,
          selectedReportId,
          report.workspaceId,
          { token }
        )
        .then(embed => setEmbedConfig(embed))
        .catch(err => console.error(err));
      }
    }
  }, [selectedReportId, token, setEmbedConfig, availableReports]);

  return (
    <div className={classes.root}>
      <ModuleAppBar breadcrumbs={[]} />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper className={classes.formSection}>
            <Grid container spacing={3}>
              {userType === UserType.Ofqual && (
                <Grid item xs={12} md={6}>
                  <SearchField
                    label="Awarding organisation"
                    disabled={false}
                    datatest="org-field"
                    searchFieldVariant="single"
                    service={mappedOrgLookupService}
                    onChange={(e) => {
                      setEmbedConfig(null);
                      setSelectedAo(e.target.value)
                    }}
                    value={selectedAo}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={userType === UserType.Ofqual ? 6 : 12}>
                <TextField
                  variant="outlined"
                  select
                  fullWidth
                  disabled={(userType === UserType.Ofqual && !selectedAo)}
                  value={selectedReportId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSelectedReportId(e.target.value);
                  }}
                  name="select-report"
                  id="select-report"
                  label="Select report"
                >
                  {availableReports.map(x => (
                    <MenuItem key={x.displayName} value={x.reportId}>
                      {x.displayName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {embedConfig && <ReportViewer embed={embedConfig} />}
        </Grid>
      </Grid>
    </div>
  );
};

export { Reports };